<template>
    <div>
        <b-form
            :id="fieldId"
            :state="isValid"
            :description="helpText"
            inline
        >
            <label class="form-label mr-2 pt-2">{{ fieldLabel }}: <slot></slot> </label>
            <input
                v-if="showDate"
                v-model="day"
                :id="dayId"
                :placeholder="$t('datefield.dayPlaceholder')"
                :disabled="disabled"
                :class="inputClass"
                class="digits-2"
                maxlength="2"
                inputmode="numeric"
                @input="inputChanged($event, dayId, 'day', 2)"
                @change="createDate"
                data-field="day"
                @focusin="focusInEvent($event, dayId, 'day')"
            >
            <span v-if="showDate" class="mr-2">/</span>
            <input
                v-if="showDate"
                v-model="month"
                :id="monthId"
                :placeholder="$t('datefield.monthPlaceholder')"
                :disabled="disabled"
                :class="inputClass"
                class="digits-2-month"
                inputmode="numeric"
                maxlength="2"
                @input="inputChanged($event, monthId, 'month', 2)"
                @change="createDate"
                @focusin="focusInEvent($event, monthId, 'month')"
            >
            <span v-if="showDate" class="mr-2">/</span>
            <input
                v-model="year"
                v-if="showDate"
                :id="yearId"
                :placeholder="$t('datefield.yearPlaceholder')"
                :disabled="disabled"
                :class="inputClass"
                class="digits-4"
                inputmode="numeric"
                maxlength="4"
                @input="inputChanged($event, yearId, 'year', 4)"
                @change="createDate"
                @focusin="focusInEvent($event, yearId, 'year')"
            >
            <span class="mr-2"></span>
            <label v-if="showTime" class="form-label mr-2 pt-2">{{ $t('datefield.hour') }}: <slot></slot> </label>
            <input
                v-model="hour"
                v-if="showTime"
                ref="hourField"
                :id="hourId"
                :placeholder="$t('datefield.hourPlaceholder')"
                :disabled="disabled"
                :class="inputClass"
                class="digits-2"
                inputmode="numeric"
                maxlength="2"
                @input="inputChanged($event, hourId, 'hour', 2)"
                @change="createDate"
                @focusin="focusInEvent($event, hourId, 'hour')"
            >
            <label v-if="showTime" class="mr-2" >:</label>
            <input
                v-if="showTime"
                v-model="minute"
                :id="minuteId"
                :placeholder="$t('datefield.minutePlaceholder')"
                :disabled="disabled"
                :class="inputClass"
                class="digits-2"
                maxlength="2"
                inputmode="numeric"
                @input="inputChanged($event, minuteId, 'minute', 2)"
                @change="createDate"
                @focusin="focusInEvent($event, minuteId, 'minute', 2)"
            >

            <b-form-invalid-feedback :state="isValid">
                {{ invalidFeedback }}
            </b-form-invalid-feedback>

        </b-form>
    </div>
</template>

<script>
    import fieldMixin from '@/components/fields/FieldMixin';
    import {isNull} from "@/utils/formatting";
    import moment from 'moment';

    export const DATE_FORMAT = 'YYYY-MM-DD';

    export default {
        name: "DateField",
        mixins: [fieldMixin],
        props: {
            datetime: {
                type: Boolean,
                default: false
            },
            time: {
                type: Boolean,
                default: false
            },
        },
        data() {
            return {
                day: '',
                month: '',
                year: '',
                hour: '',
                minute: '',
            };
        },
        computed: {
            dayId() {
                return this.prefix + 'day';
            },
            monthId() {
                return this.prefix + 'month';
            },
            yearId() {
                return this.prefix + 'year';
            },
            hourId() {
                return this.prefix + 'hour';
            },
            minuteId() {
                return this.prefix + 'minute';
            },
            prefix() {
                return this.fieldId ? this.fieldId + '-' : '';
            },
            inputClass() {
                let clazz = 'mr-2 text-center form-control px-0';
                if (this.invalidFeedback !== '') {
                    return clazz + ' error';
                }
                return clazz;
            },
            showDate() {
                return !this.time;
            },
            showTime() {
                return this.datetime || this.time;
            },
        },
        watch: {
            value(newValue) {
                this.separateValueInComponents(newValue);
            }
        },
        created() {
            this.separateValueInComponents(this.value);
        },
        methods: {
            separateValueInComponents(value) {
                if (!isNull(value)) {
                    let date = moment(this.value);
                    if (!date.isValid()) {
                        this.innerErrors = [this.$t('datefield.invalidDate')];
                    }
                    this.day = date.format('DD');
                    this.month = date.format('MM');
                    this.year = date.format('YYYY');
                    if (this.showTime) {
                        this.hour = date.format('HH');
                        this.minute = date.format('mm');

                        if (this.$refs.hourField !== undefined
                            && document.activeElement.id === this.hourId) {
                            // delay selection in order to vue to update value
                            this.$nextTick(function() {
                                this.$refs.hourField.select();
                            });
                        }
                    }
                } else {
                    this.innerErrors = [];
                }
            },
            createDate() {
                if (this.year === ''
                    && this.month === ''
                    && this.day === ''
                    && this.minute === ''
                    && this.minute === ''
                ) {
                    this.innerErrors = [];
                    this.changeValue(null);
                    return;
                }
                if (this.year === '' || this.month === '' || this.day === '') {
                    this.innerErrors = [this.$t('datefield.invalidDate')];
                    return;
                } else {
                    this.innerErrors = [];
                }
                let dateObject = {
                    year: this.year,
                    month: parseInt(this.month) - 1,
                    day: this.day,
                };
                if (this.showTime) {
                    dateObject['hour'] = this.hour;
                    dateObject['minute'] = this.minute;
                }

                const momentDate = moment(dateObject);
                if (momentDate.isValid()) {
                    this.innerErrors = [];
                    if (!this.showTime) {
                        this.changeValue(momentDate.format(DATE_FORMAT));
                    } else {
                        this.changeValue(momentDate.toISOString(true));
                    }
                } else {
                    this.innerErrors = [this.$t('datefield.invalidDate')];
                }
            },
            inputChanged(event, sourceId, field, maxLength) {
                if (event.target.id === sourceId && this[field].toString().length >= maxLength) {
                    const nextInput = this.findNextInput(event.target);
                    if (nextInput) {
                        nextInput.focus();
                    } else {
                        event.target.blur();
                    }
                }
            },
            focusInEvent(event, sourceId, field) {
                if (event.target.id === sourceId && this[field].toString().length >= 0) {
                    event.target.select();
                }
            },
            findNextInput(element) {
                let returned = element.nextElementSibling;
                while (returned !== null) {
                    if (returned instanceof HTMLInputElement) {
                        return returned;
                    }
                    returned = returned.nextElementSibling;
                }
                return null;
            },
        },
    };
</script>

<style scoped>

    input {
        color: black;
        border-top: 0;
        border-left: 0;
        border-right: 0;
        border-bottom: 1px solid black;
        border-radius: 0;
        height: 2rem;
    }

    input:disabled {
        cursor: not-allowed;
    }

    .error {
        border-bottom: 1px solid red;
    }

    .digits-2 {
        width: 1.7rem;
    }

    .digits-2-month {
        width: 2rem;
    }

    .digits-4 {
        width: 2.5rem;
    }

    ::-webkit-input-placeholder {
        text-align: center;
        color: lightgrey;
    }

    :-moz-placeholder { /* Firefox 18- */
        text-align: center;
        color: lightgrey;
    }

    ::-moz-placeholder {  /* Firefox 19+ */
        text-align: center;
        color: lightgrey;
    }

    :-ms-input-placeholder {
        text-align: center;
        color: lightgrey;
    }
</style>